import React from 'react';
import classNames from 'classnames';
import {
  Control,
  Controller,
  FieldError,
  FieldValues,
  UseFormMethods,
  RegisterOptions,
} from 'react-hook-form';
import { get } from 'lodash';
import warningImage from '../../../assets/warning.png';
import { getValidationErrorMessage } from '../../../utils/validation';
// eslint-disable-next-line import/named
import { FlattenKeys } from '../../../types/utilTypes';
import { TooltipIcon } from '../../TooltipIcon/TooltipIcon';
import styles from './styles.module.scss';

interface RadioOption<U> {
  label: string | React.ReactNode;
  tooltip?: string;
  value: U;
}

type Props<T extends FieldValues, U> = {
  className?: string;
  options: RadioOption<U>[];
} & (
  | {
      // name is required when form is set
      name: FlattenKeys<T>;
      form: UseFormMethods<T>;
      validation?: RegisterOptions;
      // component can't use a form and be controlled at the same time
      value?: undefined;
      onChange?: undefined;
    }
  | {
      name?: string;
      form?: undefined;
      // validation requires a form to be provided
      validation?: undefined;
      value: U;
      onChange: (value: U) => void;
    }
);

export const Radio = <T extends FieldValues, U = unknown>({
  className,
  name,
  options,
  form,
  validation,
  value,
  onChange,
}: Props<T, U>) => {
  const validationError = get(form?.errors, name as string) as
    | FieldError
    | undefined;

  const renderRadioButton = (
    option: RadioOption<U>,
    key: string | number,
    currentValue?: U,
    onValueChange?: (value: U) => void,
    onBlur?: () => void,
  ) => (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <label
      key={key}
      className={classNames(
        styles.container,
        !!validationError && styles.invalid,
      )}
    >
      <input
        type="radio"
        className={styles.input}
        name={name}
        checked={currentValue === option.value}
        onChange={() => onValueChange?.(option.value)}
        onBlur={onBlur}
      />
      {option.label}
      {option.tooltip && <TooltipIcon content={option.tooltip} />}
      <span className={styles.checkmark} />
    </label>
  );

  const renderInput = () => {
    if (form) {
      return (
        <Controller
          name={name as string}
          control={form.control as Control<Record<string, any>>}
          rules={validation}
          render={({
            value: currentValue,
            onChange: onValueChange,
            onBlur,
          }) => (
            <>
              {options.map((option, index) =>
                renderRadioButton(
                  option,
                  index,
                  currentValue,
                  onValueChange,
                  onBlur,
                ),
              )}
            </>
          )}
        />
      );
    }
    return options.map((option, index) =>
      renderRadioButton(option, index, value, onChange),
    );
  };

  return (
    <div className={classNames(styles.groupContainer, className)}>
      {renderInput()}

      {!!validationError && (
        <>
          <div className={styles.warningIcon}>
            <img src={warningImage} alt="Warning" />
          </div>
          <span className={styles.warningText}>
            {getValidationErrorMessage(validationError)}
          </span>
        </>
      )}
    </div>
  );
};
